import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Search } from "lucide-react";
import { api } from "../lib/apiClient";

// Debounced typeahead dropdown. Shows top matches as the viewer types.
export const SearchSuggestions = ({ query, onSelect, className = "", testid = "search-suggestions" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const q = query.trim();

  useEffect(() => {
    if (q.length < 2) {
      setItems([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get(`/search?q=${encodeURIComponent(q)}`)
        .then((r) => setItems((r.data.results || []).slice(0, 6)))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  if (q.length < 2) return null;

  const go = (v) => {
    navigate(`/watch/${v.id}`);
    onSelect?.();
  };

  return (
    <div
      data-testid={testid}
      className={`absolute top-full mt-2 z-50 rounded-sm border border-white/15 bg-[#0A0A0F]/95 backdrop-blur-xl shadow-2xl overflow-hidden ${className}`}
    >
      {loading && items.length === 0 ? (
        <div className="px-4 py-4 text-sm text-zinc-500 flex items-center gap-2">
          <span className="w-3.5 h-3.5 border-2 border-[#FF2A2A] border-t-transparent rounded-full animate-spin" />
          Searching…
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 py-4 text-sm text-zinc-500">No matches for “{q}”.</div>
      ) : (
        <>
          {items.map((v) => (
            <button
              key={v.id}
              type="button"
              data-testid={`suggestion-${v.id}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(v)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/10 transition-colors"
            >
              <div className="relative w-16 shrink-0 aspect-video rounded-sm overflow-hidden bg-black">
                <img
                  src={`https://i.ytimg.com/vi/${v.youtube_id}/default.jpg`}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <Play className="w-3.5 h-3.5 text-white fill-white" />
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{v.title}</p>
                <p className="text-[11px] text-zinc-500 uppercase tracking-wide truncate">{v.category}</p>
              </div>
            </button>
          ))}
          <button
            type="submit"
            data-testid="suggestion-see-all"
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#FF2A2A] hover:bg-white/5 border-t border-white/10 transition-colors"
          >
            <Search className="w-4 h-4" /> See all results for “{q}”
          </button>
        </>
      )}
    </div>
  );
};
